import * as MsDyn365 from '@msdyn365-commerce/core';
import React, { useEffect, useState } from 'react';
import { Toaster } from 'sonner';
import postCheckEmailVerificationTokenAction, {
    CheckEmailVerificationTokenInput
} from '../../actions/post-check-email-verification-token.action';
import postSetVerificationEmailAction, { SetVerificationEmailInput } from '../../actions/post-set-verification-email.action';
import postSubmitRegisterRequestAction, { SubmitRegisterRequestInput } from '../../actions/post-submit-register-request.action';
import { COUNTRIES } from '../../configuration/countries';
import {
    REGISTER_FIELD_ADDITIONAL_NOTES,
    REGISTER_FIELD_BUSINESS_EMAIL,
    REGISTER_FIELD_BUSINESS_UNIT,
    REGISTER_FIELD_COMPANY_NAME,
    REGISTER_FIELD_CONSENT_ACCEPTED,
    REGISTER_FIELD_COUNTRY,
    REGISTER_FIELD_FIRST_NAME,
    REGISTER_FIELD_JOB_TITLE,
    REGISTER_FIELD_LAST_NAME,
    REGISTER_FIELD_PHONE,
    REGISTER_FIELD_SOLD_TO,
    REGISTER_FIELD_TITLE,
    REGISTER_FIELD_VERIFICATION_CODE,
    REGISTER_QUERY_PARAM_BUSINESS_UNIT,
    REGISTER_QUERY_PARAM_SOLD_TO,
    REGISTER_QUERY_PARAM_TRACKING_NUMBER
} from '../../configuration/register';
import { TITLES } from '../../configuration/titles';
import { isValidOrderTrackingId } from '../../utilities';
import { BUSINESS_UNITS, mapBusinessUnitToText } from '../../utilities/map-business-unit-to-text';
import { _showToast } from '../../utilities/show-toast';
import { ILxsRegisterViewProps } from './lxs-register';
import { IRegisterSubmitPayload } from './lxs-register.data';

const LxsRegisterView: React.FC<ILxsRegisterViewProps> = ({ resources, config, context }) => {
    const CLASS_NAME = 'lxs-register';
    const optionalText = resources.lxsRegisterOptionalLabel;

    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const [businessEmail, setBusinessEmail] = useState<string>('');
    const [isSendingVerification, setIsSendingVerification] = useState<boolean>(false);
    const [verificationRequestError, setVerificationRequestError] = useState<string | null>(null);
    const [isEmailVerificationSent, setIsEmailVerificationSent] = useState<boolean>(false);

    const [verificationCode, setVerificationCode] = useState<string>('');
    const [isCheckingVerificationCode, setIsCheckingVerificationCode] = useState<boolean>(false);
    const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);

    // Sold-To and Business Unit are pre-filled from the URL query string (e.g. a link shared by
    // Sales/Support: "...?soldTo=RubberChem&bu=05"), since this is a public, unauthenticated
    // sign-up page - there is no logged-in customer profile to read these values from.
    const [soldTo, setSoldTo] = useState<string>('');
    const [businessUnit, setBusinessUnit] = useState<string>('');
    const [trackingNumber, setTrackingNumber] = useState<string>();

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const queryParams = new URLSearchParams(window.location.search);
        const soldToParam = queryParams.get(REGISTER_QUERY_PARAM_SOLD_TO);
        const businessUnitParam = queryParams.get(REGISTER_QUERY_PARAM_BUSINESS_UNIT);
        const trackingNumberParam = queryParams.get(REGISTER_QUERY_PARAM_TRACKING_NUMBER);

        if (soldToParam) {
            setSoldTo(soldToParam);
        }
        // eslint-disable-next-line security/detect-object-injection
        if (businessUnitParam && BUSINESS_UNITS[businessUnitParam]) {
            setBusinessUnit(businessUnitParam);
        }
        if (trackingNumberParam && isValidOrderTrackingId(trackingNumberParam)) {
            setTrackingNumber(trackingNumberParam);
        }
    }, []);

    const handleSendEmailVerification = async () => {
        if (!businessEmail.trim()) {
            return;
        }

        setIsSendingVerification(true);
        setVerificationRequestError(null);

        try {
            const response = await postSetVerificationEmailAction(
                new SetVerificationEmailInput(businessEmail, context.request.locale.split('-')[0]),
                context.actionContext
            );

            if (!response.success) {
                throw new Error(resources.lxsRegisterSubmitErrorText);
            }

            setIsEmailVerificationSent(true);
        } catch (error) {
            setVerificationRequestError(error instanceof Error ? error.message : resources.lxsRegisterSubmitErrorText);
        } finally {
            setIsSendingVerification(false);
        }
    };

    const showVerificationErrorToast = (message: string) => {
        _showToast(resources.lxsRegisterBusinessEmailVerificationErrorToastHeading, message, 'error');
    };

    const handleSubmitVerificationCode = async () => {
        if (!verificationCode.trim()) {
            return;
        }

        setIsCheckingVerificationCode(true);

        try {
            const response = await postCheckEmailVerificationTokenAction(
                new CheckEmailVerificationTokenInput(businessEmail, verificationCode.trim()),
                context.actionContext
            );

            if (!response.success || !response.isTokenValid) {
                showVerificationErrorToast(resources.lxsRegisterBusinessEmailVerificationInvalidCodeAlert);
                return;
            }

            if (response.existingUser) {
                showVerificationErrorToast(resources.lxsRegisterBusinessEmailVerificationEmailInUseAlert);
                return;
            }

            setIsEmailVerified(true);
            _showToast(
                resources.lxsRegisterBusinessEmailVerificationSuccessToastHeading,
                resources.lxsRegisterBusinessEmailVerificationSuccessToastText,
                'success'
            );
        } catch (error) {
            showVerificationErrorToast(resources.lxsRegisterBusinessEmailVerificationInvalidCodeAlert);
        } finally {
            setIsCheckingVerificationCode(false);
        }
    };

    const handleCancelEmailVerification = () => {
        setIsEmailVerificationSent(false);
        setVerificationCode('');
        setVerificationRequestError(null);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isEmailVerified) {
            return;
        }

        const form = event.currentTarget;

        setIsSubmitting(true);
        setSubmitError(null);

        const submittedFormData = new FormData(form);
        const payload: IRegisterSubmitPayload = {
            Title: String(submittedFormData.get(REGISTER_FIELD_TITLE) || ''),
            CompanyName: String(submittedFormData.get(REGISTER_FIELD_COMPANY_NAME) || ''),
            Country: String(submittedFormData.get(REGISTER_FIELD_COUNTRY) || ''),
            SoldTo: String(submittedFormData.get(REGISTER_FIELD_SOLD_TO) || '').trim() || undefined,
            BusinessUnit: mapBusinessUnitToText(String(submittedFormData.get(REGISTER_FIELD_BUSINESS_UNIT) || '')) || undefined,
            FirstName: String(submittedFormData.get(REGISTER_FIELD_FIRST_NAME) || ''),
            LastName: String(submittedFormData.get(REGISTER_FIELD_LAST_NAME) || ''),
            JobTitle: String(submittedFormData.get(REGISTER_FIELD_JOB_TITLE) || '').trim() || undefined,
            Email: businessEmail,
            Phone: String(submittedFormData.get(REGISTER_FIELD_PHONE) || '').trim() || undefined,
            AdditionalNotes: String(submittedFormData.get(REGISTER_FIELD_ADDITIONAL_NOTES) || '').trim() || undefined,
            ConsentAccepted: submittedFormData.get(REGISTER_FIELD_CONSENT_ACCEPTED) === 'on',
            TrackingNumber: trackingNumber
        };

        try {
            const response = await postSubmitRegisterRequestAction(new SubmitRegisterRequestInput(payload), context.actionContext);

            if (!response.success) {
                throw new Error(response.message || resources.lxsRegisterSubmitErrorText);
            }

            setIsSubmitted(true);
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : resources.lxsRegisterSubmitErrorText);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={CLASS_NAME}>
            <Toaster
                richColors
                position='top-right'
                toastOptions={{
                    classNames: {
                        default: 'toast-light',
                        error: 'toast-danger',
                        success: 'toast-success',
                        warning: 'toast-warning',
                        info: 'toast-info'
                    }
                }}
            />
            <div className={`${CLASS_NAME}__header`}>
                {config.title ? (
                    <div className={`${CLASS_NAME}__title`}>
                        <MsDyn365.RichTextComponent
                            text={config.title}
                            editProps={{
                                onEdit: event => (config.title = event.target.value),
                                requestContext: context.request
                            }}
                        />
                    </div>
                ) : (
                    <h1 className={`${CLASS_NAME}__title`}>{resources.lxsRegisterRequestPortalAccessTitle}</h1>
                )}
                {config.subtitle ? (
                    <div className={`${CLASS_NAME}__subtitle`}>
                        <MsDyn365.RichTextComponent
                            text={config.subtitle}
                            editProps={{
                                onEdit: event => (config.subtitle = event.target.value),
                                requestContext: context.request
                            }}
                        />
                    </div>
                ) : (
                    <p className={`${CLASS_NAME}__subtitle`}>{resources.lxsRegisterRequestPortalAccessSubtitle}</p>
                )}
            </div>

            {isSubmitted ? (
                <div className={`${CLASS_NAME}__confirmation`} role='status'>
                    <span className={`${CLASS_NAME}__confirmation-icon`} aria-hidden='true' />
                    <p className={`${CLASS_NAME}__confirmation-text`}>{resources.lxsRegisterSubmitSuccessText}</p>
                </div>
            ) : (
                <form className={`${CLASS_NAME}__form`} onSubmit={handleSubmit} aria-busy={isSubmitting}>
                    <section className={`${CLASS_NAME}__section`}>
                        <h2 className={`${CLASS_NAME}__section-title`}>{resources.lxsRegisterBusinessEmailVerificationSectionTitle}</h2>
                        <div className={`msc-form__group ${CLASS_NAME}__email-group`}>
                            <label className='msc-form-label' htmlFor='lxs-register-business-email'>
                                {resources.lxsRegisterBusinessEmailLabel}
                            </label>
                            <div className={`${CLASS_NAME}__input-group`}>
                                <input
                                    id='lxs-register-business-email'
                                    name={REGISTER_FIELD_BUSINESS_EMAIL}
                                    className='msc-form-control'
                                    type='email'
                                    autoComplete='email'
                                    placeholder={resources.lxsRegisterBusinessEmailPlaceholder}
                                    required={true}
                                    value={businessEmail}
                                    onChange={event => setBusinessEmail(event.target.value)}
                                    disabled={isEmailVerificationSent}
                                />
                                {isEmailVerified ? (
                                    <p className={`${CLASS_NAME}__verified ${CLASS_NAME}__success`} role='status'>
                                        {resources.lxsRegisterBusinessEmailVerificationSuccessToastHeading}
                                    </p>
                                ) : (
                                    <button
                                        className='msc-btn msc-loading-btn msc-cta__primary'
                                        type='button'
                                        disabled={isSendingVerification || isEmailVerificationSent || !businessEmail.trim()}
                                        onClick={handleSendEmailVerification}
                                    >
                                        <span
                                            className={`msc-loading-btn__spinner ${
                                                isSendingVerification ? '' : `${CLASS_NAME}__spinner--idle`
                                            }`}
                                        />
                                        <span>{resources.lxsRegisterBusinessEmailVerificationButtonPlaceholder}</span>
                                    </button>
                                )}
                            </div>
                            {verificationRequestError && (
                                <p className={`${CLASS_NAME}__alert ${CLASS_NAME}__error`} role='alert'>
                                    {verificationRequestError}
                                </p>
                            )}
                        </div>

                        {isEmailVerificationSent && !isEmailVerified && (
                            <div className={`${CLASS_NAME}__verification-box`}>
                                <div className={`${CLASS_NAME}__input-group`}>
                                    <input
                                        id='lxs-register-verification-code'
                                        name={REGISTER_FIELD_VERIFICATION_CODE}
                                        className='msc-form-control'
                                        type='text'
                                        inputMode='numeric'
                                        aria-label={resources.lxsRegisterBusinessEmailVerificationCodeInputPlaceholder}
                                        placeholder={resources.lxsRegisterBusinessEmailVerificationCodeInputPlaceholder}
                                        value={verificationCode}
                                        onChange={event => setVerificationCode(event.target.value)}
                                    />
                                    <div className={`${CLASS_NAME}__verification-actions`}>
                                        <button
                                            className='msc-btn msc-loading-btn msc-cta__primary'
                                            type='button'
                                            disabled={isCheckingVerificationCode || !verificationCode.trim()}
                                            onClick={handleSubmitVerificationCode}
                                        >
                                            <span
                                                className={`msc-loading-btn__spinner ${
                                                    isCheckingVerificationCode ? '' : `${CLASS_NAME}__spinner--idle`
                                                }`}
                                            />
                                            <span>{resources.lxsRegisterBusinessEmailVerificationSubmitCodeButtonPlaceholder}</span>
                                        </button>
                                        <button
                                            className='msc-btn msc-cta__secondary'
                                            type='button'
                                            disabled={isCheckingVerificationCode}
                                            onClick={handleCancelEmailVerification}
                                        >
                                            {resources.lxsRegisterBusinessEmailVerificationCancelButtonText}
                                        </button>
                                    </div>
                                </div>
                                <p className={`${CLASS_NAME}__verification-hint`}>
                                    {resources.lxsRegisterBusinessEmailVerificationVerificationCode}
                                </p>
                            </div>
                        )}
                    </section>

                    <section className={`${CLASS_NAME}__section`}>
                        <h2 className={`${CLASS_NAME}__section-title`}>{resources.lxsRegisterContactPersonSectionTitle}</h2>

                        <div className={`${CLASS_NAME}__grid`}>
                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-title'>
                                    {resources.lxsRegisterTitleLabel}
                                </label>
                                <select
                                    id='lxs-register-title'
                                    name={REGISTER_FIELD_TITLE}
                                    className='msc-form-control'
                                    autoComplete='honorific-prefix'
                                    defaultValue=''
                                    required={true}
                                >
                                    <option value='' disabled={true}>
                                        {resources.lxsRegisterTitlePlaceholder}
                                    </option>
                                    {TITLES.map(title => (
                                        <option key={title.value} value={title.value}>
                                            {resources[title.resourceKey]}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-first-name'>
                                    {resources.lxsRegisterFirstNameLabel}
                                </label>
                                <input
                                    id='lxs-register-first-name'
                                    name={REGISTER_FIELD_FIRST_NAME}
                                    className='msc-form-control'
                                    type='text'
                                    autoComplete='given-name'
                                    placeholder={resources.lxsRegisterFirstNamePlaceholder}
                                    required={true}
                                />
                            </div>

                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-last-name'>
                                    {resources.lxsRegisterLastNameLabel}
                                </label>
                                <input
                                    id='lxs-register-last-name'
                                    name={REGISTER_FIELD_LAST_NAME}
                                    className='msc-form-control'
                                    type='text'
                                    autoComplete='family-name'
                                    placeholder={resources.lxsRegisterLastNamePlaceholder}
                                    required={true}
                                />
                            </div>

                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-job-title'>
                                    {resources.lxsRegisterJobTitleLabel}
                                    <span className={`${CLASS_NAME}__optional`}>{optionalText}</span>
                                </label>
                                <input
                                    id='lxs-register-job-title'
                                    name={REGISTER_FIELD_JOB_TITLE}
                                    className='msc-form-control'
                                    type='text'
                                    autoComplete='organization-title'
                                    placeholder={resources.lxsRegisterJobTitlePlaceholder}
                                />
                            </div>

                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-phone'>
                                    {resources.lxsRegisterPhoneLabel}
                                    <span className={`${CLASS_NAME}__optional`}>{optionalText}</span>
                                </label>
                                <input
                                    id='lxs-register-phone'
                                    name={REGISTER_FIELD_PHONE}
                                    className='msc-form-control'
                                    type='tel'
                                    autoComplete='tel'
                                    placeholder={resources.lxsRegisterPhonePlaceholder}
                                />
                            </div>
                        </div>
                    </section>

                    <section className={`${CLASS_NAME}__section`}>
                        <h2 className={`${CLASS_NAME}__section-title`}>{resources.lxsRegisterCompanySectionTitle}</h2>

                        <div className={`${CLASS_NAME}__grid`}>
                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-company-name'>
                                    {resources.lxsRegisterCompanyNameLabel}
                                </label>
                                <input
                                    id='lxs-register-company-name'
                                    name={REGISTER_FIELD_COMPANY_NAME}
                                    className='msc-form-control'
                                    type='text'
                                    autoComplete='organization'
                                    placeholder={resources.lxsRegisterCompanyNamePlaceholder}
                                    required={true}
                                />
                            </div>

                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-sold-to'>
                                    {resources.lxsRegisterSoldToLabel}
                                    <span className={`${CLASS_NAME}__optional`}>{optionalText}</span>
                                </label>
                                <input
                                    id='lxs-register-sold-to'
                                    name={REGISTER_FIELD_SOLD_TO}
                                    className='msc-form-control'
                                    type='text'
                                    placeholder={resources.lxsRegisterSoldToPlaceholder}
                                    value={soldTo}
                                    onChange={event => setSoldTo(event.target.value)}
                                />
                            </div>

                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-country'>
                                    {resources.lxsRegisterCountryLabel}
                                </label>
                                <select
                                    id='lxs-register-country'
                                    name={REGISTER_FIELD_COUNTRY}
                                    className='msc-form-control'
                                    autoComplete='country-name'
                                    defaultValue=''
                                    required={true}
                                >
                                    <option value='' disabled={true}>
                                        {resources.lxsRegisterCountryPlaceholder}
                                    </option>
                                    {COUNTRIES.map(country => (
                                        <option key={country.value} value={country.value}>
                                            {resources[country.resourceKey]}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className='msc-form__group'>
                                <label className='msc-form-label' htmlFor='lxs-register-business-unit'>
                                    {resources.lxsRegisterBusinessUnitLabel}
                                    <span className={`${CLASS_NAME}__optional`}>{optionalText}</span>
                                </label>
                                <select
                                    id='lxs-register-business-unit'
                                    name={REGISTER_FIELD_BUSINESS_UNIT}
                                    className='msc-form-control'
                                    value={businessUnit}
                                    onChange={event => setBusinessUnit(event.target.value)}
                                >
                                    <option value=''>{resources.lxsRegisterBusinessUnitPlaceholder}</option>
                                    {Object.entries(BUSINESS_UNITS).map(([code, label]) => (
                                        <option key={code} value={code}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </section>

                    <section className={`${CLASS_NAME}__section`}>
                        <div className={`${CLASS_NAME}__grid`}>
                            <div className={`msc-form__group ${CLASS_NAME}__field--full`}>
                                <label className='msc-form-label' htmlFor='lxs-register-notes'>
                                    {resources.lxsRegisterAdditionalNotesLabel}
                                    <span className={`${CLASS_NAME}__optional`}>{optionalText}</span>
                                </label>
                                <textarea
                                    id='lxs-register-notes'
                                    name={REGISTER_FIELD_ADDITIONAL_NOTES}
                                    className='msc-form-control'
                                    rows={4}
                                    placeholder={resources.lxsRegisterAdditionalNotesPlaceholder}
                                />
                            </div>
                        </div>

                        <label className={`${CLASS_NAME}__consent`} htmlFor='lxs-register-consent'>
                            <input id='lxs-register-consent' name={REGISTER_FIELD_CONSENT_ACCEPTED} type='checkbox' required={true} />
                            <span>
                                {`${resources.lxsRegisterConsentLabel} `}
                                <a
                                    className={`${CLASS_NAME}__consent-link`}
                                    href={resources.lxsRegisterPrivacyPolicyLinkUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {resources.lxsRegisterPrivacyPolicyLinkText}
                                </a>
                            </span>
                        </label>
                    </section>

                    {submitError && (
                        <p className={`${CLASS_NAME}__status ${CLASS_NAME}__error`} role='alert'>
                            {submitError}
                        </p>
                    )}

                    <div className={`${CLASS_NAME}__actions`}>
                        <button
                            className='msc-btn msc-loading-btn msc-cta__primary'
                            type='submit'
                            disabled={isSubmitting || !isEmailVerified}
                        >
                            {isSubmitting && <span className='msc-loading-btn__spinner mr-2' />}
                            <span>{isSubmitting ? resources.lxsRegisterSubmitLoadingText : resources.lxsRegisterSubmitButtonText}</span>
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default LxsRegisterView;
